/* ============================================================
 * <lion-collection-results> — grid container whose contents (grid,
 *   pagination, filters, count) are swapped via the Section
 *   Rendering API when filters/sort change or load-more fires.
 *
 * <collection-filters> — opens/closes the filter panel on mobile.
 *   (The form fields themselves are inside <lion-collection-results>.)
 *
 * Filter/sort flow:
 *   1. Listen for form change / sort select change / load-more click.
 *   2. Build new URL from form + URL search params.
 *   3. Fetch `${url.pathname}?${url.search}&section_id=X`.
 *   4. Parse HTML, swap inner of <lion-collection-results>.
 *   5. history.pushState with the new URL.
 * ============================================================ */

class CollectionResults extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.closest('[data-collection]')?.id
      || this.closest('section')?.getAttribute('id')
      || null;
    // Fall back: read the Shopify section wrapper's id
    if (!this.sectionId) {
      const parent = this.closest('.shopify-section');
      if (parent) this.sectionId = parent.id.replace('shopify-section-', '');
    }

    this.abortController = null;

    this.addEventListener('change', (e) => this.onChange(e));
    this.addEventListener('input', (e) => this.onInput(e));
    this.addEventListener('submit', (e) => this.onSubmit(e));
    this.addEventListener('click', (e) => this.onClick(e));

    window.addEventListener('popstate', () => this.renderFromUrl(window.location.search));
  }

  onChange(event) {
    const target = event.target;
    // Sort select
    if (target.matches('[data-collection-sort]')) {
      this.applyFilters(new FormData(this.filterForm), { sort: target.value, page: null });
      return;
    }
    // Filter form fields (checkboxes)
    if (target.closest('[data-collection-filters-form]') && target.type === 'checkbox') {
      this.applyFilters(new FormData(this.filterForm), { page: null });
    }
  }

  onInput(event) {
    // Debounced price-range update
    const target = event.target;
    if (!target.closest('[data-collection-filters-form]')) return;
    if (target.type !== 'number') return;
    clearTimeout(this._priceTimer);
    this._priceTimer = setTimeout(() => {
      this.applyFilters(new FormData(this.filterForm), { page: null });
    }, 500);
  }

  onSubmit(event) {
    if (!event.target.matches('[data-collection-filters-form]')) return;
    event.preventDefault();
    this.applyFilters(new FormData(this.filterForm), { page: null });
  }

  onClick(event) {
    const clearBtn = event.target.closest('[data-filters-clear]');
    if (clearBtn) {
      event.preventDefault();
      const url = new URL(clearBtn.href, window.location.origin);
      this.renderFromUrl(url.search, { pushHistory: true, href: clearBtn.href });
      return;
    }

    const loadMore = event.target.closest('[data-collection-load-more]');
    if (loadMore) {
      event.preventDefault();
      this.appendFromUrl(loadMore.href);
    }

    const filterToggle = event.target.closest('[data-filter-toggle]');
    if (filterToggle) {
      const panel = this.querySelector('[data-collection-filters]');
      if (!panel) return;
      const isOpen = panel.classList.toggle('is-open');
      filterToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    if (event.target.closest('[data-filters-close]')) {
      const panel = this.querySelector('[data-collection-filters]');
      panel?.classList.remove('is-open');
    }
  }

  get filterForm() {
    return this.querySelector('[data-collection-filters-form]');
  }

  applyFilters(formData, overrides = {}) {
    const params = new URLSearchParams();
    if (formData) {
      for (const [k, v] of formData.entries()) {
        if (v === '' || v === null) continue;
        params.append(k, v);
      }
    }
    const sortSelect = this.querySelector('[data-collection-sort]');
    const sort = overrides.sort ?? sortSelect?.value;
    if (sort) params.set('sort_by', sort);
    if (overrides.page === null) params.delete('page');
    this.renderFromUrl('?' + params.toString(), { pushHistory: true });
  }

  async renderFromUrl(search, { pushHistory = false, href = null } = {}) {
    this.setLoading(true);
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const url = href
      ? new URL(href, window.location.origin)
      : new URL(window.location.pathname + search, window.location.origin);

    try {
      const fetchUrl = `${url.pathname}${url.search}${url.search ? '&' : '?'}section_id=${this.sectionId}`;
      const res = await fetch(fetchUrl, { signal: this.abortController.signal });
      if (!res.ok) throw new Error(res.statusText);
      const html = await res.text();
      this.swapContent(html);
      if (pushHistory) history.pushState({}, '', url.toString());
    } catch (err) {
      if (err.name !== 'AbortError') console.error('[collection] filter failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  async appendFromUrl(nextUrl) {
    this.setLoading(true);
    try {
      const url = new URL(nextUrl, window.location.origin);
      const fetchUrl = `${url.pathname}${url.search}${url.search ? '&' : '?'}section_id=${this.sectionId}`;
      const res = await fetch(fetchUrl);
      if (!res.ok) throw new Error(res.statusText);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newItems = doc.querySelectorAll('[data-collection-grid] > li');
      const grid = this.querySelector('[data-collection-grid]');
      const newLoadMore = doc.querySelector('[data-collection-load-more]');
      const currentLoadMore = this.querySelector('[data-collection-load-more]');
      if (grid && newItems.length) {
        newItems.forEach((el) => grid.appendChild(el));
      }
      if (currentLoadMore) {
        if (newLoadMore) currentLoadMore.href = newLoadMore.href;
        else currentLoadMore.closest('.collection__load-more-wrap')?.remove();
      }
    } catch (err) {
      console.error('[collection] load more failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  swapContent(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const fresh = doc.querySelector('lion-collection-results');
    if (!fresh) return;
    // Preserve focus so filter checkboxes don't steal it mid-change
    const active = document.activeElement;
    const activeKey = active ? this.keyFor(active) : null;
    this.innerHTML = fresh.innerHTML;
    if (activeKey) {
      const restored = this.querySelector(`[data-focus-key="${activeKey}"]`)
        || this.queryByKey(activeKey);
      restored?.focus?.();
    }
  }

  keyFor(el) {
    if (!el) return null;
    if (el.id) return `id:${el.id}`;
    if (el.name && el.value) return `nv:${el.name}=${el.value}`;
    return null;
  }

  queryByKey(key) {
    if (key.startsWith('id:')) return this.querySelector(`#${CSS.escape(key.slice(3))}`);
    if (key.startsWith('nv:')) {
      const [name, value] = key.slice(3).split('=');
      return this.querySelector(`[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
    }
    return null;
  }

  setLoading(isLoading) {
    const loading = this.querySelector('[data-collection-loading]');
    if (loading) {
      if (isLoading) {
        const template = this.querySelector('[data-collection-skeleton]');
        if (template && !loading.firstElementChild) {
          loading.appendChild(template.content.cloneNode(true));
        }
        loading.setAttribute('aria-busy', 'true');
      } else {
        loading.replaceChildren();
        loading.setAttribute('aria-busy', 'false');
      }
    }
    this.classList.toggle('is-loading', isLoading);
  }
}

if (!customElements.get('lion-collection-results')) {
  customElements.define('lion-collection-results', CollectionResults);
}
